import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import {enUS, ptBR, esES} from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import BrandingContext from "./context/Branding/BrandingContext";
import api from "./services/api";
import BrandingSplash from "./components/BrandingSplash";
import { i18n } from "./translate/i18n";

import Routes from "./routes";

const queryClient = new QueryClient();
const defaultBranding = {
  siteName: "",
  primaryColor: "#2576d2",
  logoUrl: "",
  gradientStart: "",
  gradientEnd: ""
};

const normalizeBranding = (rawBranding = {}) => {
  const next = {
    ...defaultBranding,
    ...rawBranding
  };
  const logo = next.logoUrl ? String(next.logoUrl) : "";
  next.logoUrl =
    logo && logo.startsWith("/")
      ? `${process.env.REACT_APP_BACKEND_URL}${logo}`
      : logo;
  return next;
};

const App = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
    const storedBranding = React.useMemo(() => {
        try {
            const cached = window.localStorage.getItem("branding");
            const parsed = cached ? JSON.parse(cached) : null;
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    }, []);
    const [branding, setBranding] = useState(() =>
        storedBranding ? normalizeBranding(storedBranding) : defaultBranding
    );
    const [brandingLoaded, setBrandingLoaded] = useState(() => Boolean(storedBranding));
    const [brandingError, setBrandingError] = useState(false);
    const brandingReadyRef = React.useRef(Boolean(storedBranding));
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
      {
        shape: {
          borderRadius: 16,
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
            backgroundColor: branding.primaryColor || "#2576d2",
          },
        },
        scrollbarStylesSoft: {
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
          },
        },
        palette: {
          type: mode,
          primary: { main: mode === "light" ? (branding.primaryColor || "#2576d2") : "#FFFFFF" },
          textPrimary: mode === "light" ? (branding.primaryColor || "#2576d2") : "#FFFFFF",
          borderPrimary: mode === "light" ? (branding.primaryColor || "#2576d2") : "#FFFFFF",
          dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
          light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
          tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
          optionsBackground: mode === "light" ? "#fafafa" : "#333",
          options: mode === "light" ? "#fafafa" : "#666",
          fontecor: mode === "light" ? "#128c7e" : "#fff",
          fancyBackground: mode === "light" ? "#fafafa" : "#333",
          bordabox: mode === "light" ? "#eee" : "#333",
          newmessagebox: mode === "light" ? "#eee" : "#333",
          inputdigita: mode === "light" ? "#fff" : "#666",
          contactdrawer: mode === "light" ? "#fff" : "#666",
          announcements: mode === "light" ? "#ededed" : "#333",
          login: mode === "light" ? "#fff" : "#1C1C1C",
          announcementspopover: mode === "light" ? "#fff" : "#666",
          chatlist: mode === "light" ? "#eee" : "#666",
          boxlist: mode === "light" ? "#ededed" : "#666",
          boxchatlist: mode === "light" ? "#ededed" : "#333",
          total: mode === "light" ? "#fff" : "#222",
          messageIcons: mode === "light" ? "grey" : "#F3F3F3",
          inputBackground: mode === "light" ? "#FFFFFF" : "#333",
          barraSuperior:
            mode === "light"
              ? `linear-gradient(to right, ${branding.gradientStart || branding.primaryColor || "#2576d2"}, ${branding.gradientEnd || "#0b5cb8"})`
              : "#666",
          boxticket: mode === "light" ? "#EEE" : "#666",
          campaigntab: mode === "light" ? "#ededed" : "#666",
          mediainput: mode === "light" ? "#ededed" : "#1c1c1c",
        },
        mode,
      },
      locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt"){
            setLocale(ptBR);
        }else if( browserLocale === "en" ) {
            setLocale(enUS)
        }else if( browserLocale === "es" )
            setLocale(esES)

    }, []);

    useEffect(() => {
        const handleLanguageChanged = (lng) => {
            setCurrentLanguage(lng);
        };

        i18n.on("languageChanged", handleLanguageChanged);
        return () => {
            i18n.off("languageChanged", handleLanguageChanged);
        };
    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);

    const fetchBranding = React.useCallback(async () => {
        setBrandingError(false);
        try {
            let data = [];
            try {
                const resp = await api.get("/settings");
                data = resp.data;
            } catch {
                const resp = await api.get("/public/branding");
                data = resp.data || [];
            }
            const collected = {};
            data.forEach((s) => {
                if (s.key === "branding.siteName") collected.siteName = s.value;
                if (s.key === "branding.primaryColor") collected.primaryColor = s.value;
                if (s.key === "branding.logoUrl") collected.logoUrl = s.value;
                if (s.key === "branding.gradientStart") collected.gradientStart = s.value;
                if (s.key === "branding.gradientEnd") collected.gradientEnd = s.value;
            });
            setBranding((prev) => {
                const merged = normalizeBranding({ ...prev, ...collected });
                try {
                    window.localStorage.setItem("branding", JSON.stringify(merged));
                } catch {
                    // ignore storage errors
                }
                return merged;
            });
            brandingReadyRef.current = true;
            setBrandingLoaded(true);
        } catch (e) {
            if (!brandingReadyRef.current) {
                setBrandingError(true);
                setBrandingLoaded(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchBranding();

        const companyId = localStorage.getItem("companyId");
        if (!companyId) {
            return undefined;
        }
        const socket = SocketManager.getSocket(companyId);
        socket.on(`company-${companyId}-settings`, (payload) => {
            if (payload?.action === "update" && payload.setting?.key) {
                const { key, value } = payload.setting;
                setBranding((prev) => {
                    const next = { ...prev };
                    if (key === "branding.siteName") next.siteName = value;
                    else if (key === "branding.primaryColor") next.primaryColor = value;
                    else if (key === "branding.logoUrl") next.logoUrl = value;
                    else if (key === "branding.gradientStart") next.gradientStart = value;
                    else if (key === "branding.gradientEnd") next.gradientEnd = value;
                    const normalized = normalizeBranding(next);
                    try {
                        window.localStorage.setItem("branding", JSON.stringify(normalized));
                    } catch {
                        // ignore storage errors
                    }
                    return normalized;
                });
                brandingReadyRef.current = true;
                setBrandingLoaded(true);
            }
        });

        return () => {
            socket.disconnect();
        };
    }, [fetchBranding]);

    useEffect(() => {
        if (!brandingLoaded) {
            document.title = i18n.t("brandingSplash.loadingDocumentTitle");
            return;
        }
        if (branding?.siteName) {
            document.title = branding.siteName;
        } else {
            document.title = i18n.t("brandingSplash.defaultDocumentTitle");
        }
    }, [brandingLoaded, branding?.siteName, currentLanguage]);

    if (!brandingLoaded) {
        return (
            <BrandingSplash
                variant={brandingError ? "error" : "loading"}
                onRetry={brandingError ? fetchBranding : undefined}
            />
        );
    }

    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <BrandingContext.Provider value={branding}>
              <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                  </SocketContext.Provider>
                </QueryClientProvider>
              </ThemeProvider>
            </BrandingContext.Provider>
        </ColorModeContext.Provider>
    );
};

export default App;
